import { ref } from 'vue'
import ApiService from '@/service/api.service'
import { orderBy as _orderBy } from 'lodash-es'

/*
 * This hook fetches all policies for the company and all policies that exist for a product.
 * It then combines the two to create a list of policy options for the product and exposes the list.
 * NOTE: This should ideally be done in the API, but there is no endpoint that provides this data.
 * As the code has been quite problematic to get right and without race conditions previously,
 * i've wrapped this up into a single source that provides a finite state as if it came from the API.
 */
export function useGetPolicyOptionsByProductId(productId) {
  const policyOptions = ref([])
  const isPolicyOptionsLoading = ref(true)

  const fetchProductPolicies = async () => {
    if (!productId) return []

    try {
      const response = await ApiService.getProductPolicies(productId)
      return response.data
    } catch (err) {
      console.error('Error fetching product policies:', err)
    }
  }

  const getAllPolicies = async () => {
    const sortPolicies = (policies) => {
      return _orderBy(policies, ['base_content', 'name'], ['desc', 'asc'])
    }

    try {
      const response = await ApiService.getAllPolicies()
      const policies = response.data
      // Should have come sorted from the API, but since that's not the case, we emulate it here
      return sortPolicies(policies)
    } catch (error) {
      console.error('Error fetching policies:', error)
    }
  }

  async function getPolicyOptions() {
    isPolicyOptionsLoading.value = true

    await Promise.all([fetchProductPolicies(), getAllPolicies()])
      .then(([productPolicies, allPolicies]) => {
        policyOptions.value = allPolicies.map((policy) => {
          const existingPolicy = productPolicies.find(
            (existingPolicy) => existingPolicy.id === policy.id
          )

          return {
            id: policy.id,
            isBaseContentPolicy: policy.base_content,
            name: policy.name,
            value: existingPolicy ? existingPolicy.state : undefined,
          }
        })
      })
      .finally((isPolicyOptionsLoading.value = false))
  }

  getPolicyOptions()

  return {
    policyOptions,
    isLoading: isPolicyOptionsLoading,
    mutate: getPolicyOptions,
  }
}
